/**
 * SleeveIntro — the "a feeling has arrived" overlay shown for ~1.6s before
 * the sleeve appears on /open/:slug or /c/:slug.
 *
 * Phase 4·e (combined with section-3 Safe-Open Layer from the user spec):
 *   • Turns the URL itself into theatre — the slug ("rain-laughter-ghost")
 *     deconstructs into poetic phrase ("rain · laughter · ghost") with
 *     staggered word-by-word fade-ins.
 *   • Doubles as the Safe Open intermediary — reduces phishing fear with
 *     subtle trust hints (private · safe · no signup) before reveal.
 *   • Auto-dismisses after ~1800ms. Tap-anywhere skips immediately.
 *   • Only shown on FIRST visit per slug (sessionStorage gate) so subsequent
 *     reloads don't re-play and feel laggy.
 *
 * Restrained: alive-minimalism rules apply. No bright colors, no particles,
 * no sounds. Type fade + a soft amber pulse-dot. That's it.
 */
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const SHOWN_KEY = "moodcard.intro.seen";

function _wordsFromSlug(slug) {
    if (!slug) return [];
    // "rain-laughter-ghost" → ["rain", "laughter", "ghost"]
    // Numbers (rare collision suffixes like "-7q") get stripped so they
    // don't pollute the poetry.
    return slug.split("-").filter((w) => /^[a-z]+$/i.test(w)).slice(0, 5);
}

function _alreadyShown(slug) {
    if (typeof window === "undefined") return false;
    try {
        const seen = JSON.parse(window.sessionStorage.getItem(SHOWN_KEY) || "{}");
        return !!seen[slug];
    } catch {
        return false;
    }
}

function _markShown(slug) {
    if (typeof window === "undefined") return;
    try {
        const seen = JSON.parse(window.sessionStorage.getItem(SHOWN_KEY) || "{}");
        seen[slug] = Date.now();
        window.sessionStorage.setItem(SHOWN_KEY, JSON.stringify(seen));
    } catch { /* ignore */ }
}

export default function SleeveIntro({ slug, onDone, durationMs = 3400, recipient = null }) {
    // Compute initial visibility synchronously so we don't flash the sleeve
    // for one frame before showing the intro.
    const [open, setOpen] = useState(() => slug && !_alreadyShown(slug));
    const words = _wordsFromSlug(slug);

    useEffect(() => {
        if (!open) {
            // Skip immediately — call onDone next tick so callers can mount sleeve.
            const t = setTimeout(() => onDone && onDone(), 0);
            return () => clearTimeout(t);
        }
        const t = setTimeout(() => {
            _markShown(slug);
            setOpen(false);
            onDone && onDone();
        }, durationMs);
        return () => clearTimeout(t);
    }, [open, slug, durationMs, onDone]);

    const skip = () => {
        if (!open) return;
        _markShown(slug);
        setOpen(false);
        onDone && onDone();
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{
                        duration: 0.5,
                        ease: [0.16, 1, 0.3, 1],
                        // Longer, smoother exit so the poem doesn't "drift away"
                        // mid-read. The slug words and trust hints now hold their
                        // composure right up until they vanish in unison.
                        exit: { duration: 1.1, ease: [0.45, 0, 0.55, 1] },
                    }}
                    onClick={skip}
                    data-testid="sleeve-intro-overlay"
                    className="fixed inset-0 z-[60] flex flex-col items-center justify-center cursor-pointer"
                    style={{
                        background: "radial-gradient(120% 100% at 50% 50%, #1a0a0e 0%, #050507 70%)",
                    }}
                >
                    {/* Soft amber pulse-dot — sole signal of life on this otherwise still screen */}
                    <motion.div
                        animate={{ opacity: [0.4, 0.9, 0.4], scale: [0.85, 1.05, 0.85] }}
                        transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
                        className="h-1.5 w-1.5 rounded-full mb-6"
                        style={{ background: "#d4af6e", boxShadow: "0 0 16px rgba(212, 175, 110, 0.55)" }}
                    />

                    {/* "for <Name>" — only when sender addressed the share. Trimmed
                        + length-capped + Capital-Cased so junk can't poison the moment.
                        Appears BEFORE "a feeling has arrived" so the recipient sees
                        their name first — the hand-addressed feel that lifts SMS opens. */}
                    {recipient && (
                        <motion.div
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
                            data-testid="sleeve-intro-recipient"
                            className="mb-3 flex items-baseline gap-2"
                        >
                            <span className="font-sans text-[9px] uppercase tracking-[0.4em] text-amber-200/60">for</span>
                            <span
                                className="font-serif italic text-amber-100/90"
                                style={{ fontSize: "22px", letterSpacing: "0.02em" }}
                            >
                                {recipient}
                            </span>
                        </motion.div>
                    )}

                    {/* "a feeling has arrived" header */}
                    <motion.span
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                        className="font-sans text-[10px] uppercase tracking-[0.45em] text-amber-200/70 mb-5"
                    >
                        a feeling has arrived
                    </motion.span>

                    {/* The slug deconstructed into poetic phrase — words now
                        arrive together in one calm reveal instead of staggered
                        cascade. Stabilizes the "drifting away" feel the user
                        flagged when text appeared and disappeared at varied
                        offsets. Still soft, still cinematic; just steadier. */}
                    <motion.div
                        initial={{ opacity: 0, y: 8, filter: "blur(5px)" }}
                        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                        className="flex flex-wrap items-baseline justify-center gap-x-4 px-6 max-w-[90vw]"
                        data-testid="sleeve-intro-poetry"
                    >
                        {words.length > 0 ? words.map((w, i) => (
                            <React.Fragment key={`${w}-${i}`}>
                                <span
                                    className="font-serif italic text-3xl sm:text-4xl text-app"
                                    style={{ color: "#ece5d8" }}
                                >
                                    {w}
                                </span>
                                {i < words.length - 1 && (
                                    <span className="font-serif text-3xl sm:text-4xl text-amber-200/40 -mx-1">
                                        ·
                                    </span>
                                )}
                            </React.Fragment>
                        )) : (
                            <span className="font-serif italic text-3xl text-app">
                                someone left a feeling
                            </span>
                        )}
                    </motion.div>

                    {/* Trust hints — Safe Open layer from the spec, ultra subtle.
                        Now uses a fixed delay (no per-word offset) so trust hints
                        always land together once the words have settled. */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.7 }}
                        transition={{ duration: 0.6, delay: 1.2 }}
                        className="mt-10 flex items-center gap-3 text-[10px] uppercase tracking-[0.32em] text-app-3"
                    >
                        <span>safe</span>
                        <span className="opacity-50">·</span>
                        <span>private</span>
                        <span className="opacity-50">·</span>
                        <span>no signup</span>
                    </motion.div>

                    {/* Tiny tap-anywhere hint */}
                    <motion.span
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 0.45 }}
                        transition={{ duration: 0.5, delay: 1.5 }}
                        className="absolute bottom-10 font-serif italic text-[12px] text-amber-100/60"
                    >
                        tap to open gently
                    </motion.span>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
